import React from "react";
import { FormControl, FormLabel, Input, Select, Option, Textarea, InputProps, SelectProps as JoySelectProps, TextareaProps, selectClasses, Box } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { RadioGroup, Radio } from "@mui/joy";
import { RadioInputProps } from "./types";

interface BaseFieldProps {
  name: string;
  label: string;
  required?: boolean;
  formControlSx?: SxProps;
}

export interface TextInputProps extends BaseFieldProps {
  value: string | number; // Or just string if numbers are always handled as strings in state
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute; // 'text', 'number', 'date', etc.
  endDecorator?: React.ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  inputSx?: SxProps;
  inputProps?: Omit<InputProps, "name" | "value" | "onChange" | "placeholder" | "type" | "endDecorator" | "readOnly" | "disabled" | "sx">;
  formatCurrency?: boolean;
  autocomplete?: string;
  noBold?: boolean;
  className?: string;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  maxLength?: any;
}

export const TextInput: React.FC<TextInputProps> = ({
  name,
  label,
  value,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  type = "text",
  required,
  endDecorator,
  readOnly,
  disabled,
  formControlSx,
  inputSx,
  inputProps,
  autocomplete,
  noBold,
  className,
  formatCurrency,
  maxLength
}) => {

  return (
    <FormControl required={required} sx={formControlSx}>
      <FormLabel sx={{ ...(noBold ? {} : { fontWeight: "bold" }) }}>{label}</FormLabel>
      <Input
        name={name}
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        type={type}
        endDecorator={endDecorator}
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        onFocus={onFocus}
        sx={inputSx}
        {...inputProps}
        slotProps={{
          input: maxLength ? {
            maxLength: maxLength,
          } : {},
        }}

      />
    </FormControl>
  );
};

export interface SelectOption {
  value: string;
  label: string;
}

// Define the type for the 'rest' props using Omit with specified generics
type CustomSelectRestProps = Omit<
  JoySelectProps<string, false, "button">, // <OptionValue is string, Multiple is false, D is default 'button'>
  "name" | "value" | "onChange" | "placeholder" | "disabled" | "sx" | "children"
>;

interface SelectInputProps extends BaseFieldProps {
  value: string | null;
  onChange: (name: string, value: string | null) => void; // Name is passed for generic handler
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  loading?: boolean; // Add loading prop
  loadingText?: string; // Customizable loading text
  selectSx?: SxProps;
  selectProps?: CustomSelectRestProps; // Use the Omit-based type
}

export const SelectInput: React.FC<SelectInputProps> = ({
  name,
  label,
  value,
  onChange,
  placeholder,
  options,
  required,
  disabled,
  loading = false,
  loadingText = "Loading...",
  formControlSx,
  selectSx,
  selectProps,
}) => {
  // Show loading option when loading, otherwise show normal options
  const allOptions = loading ? [{ value: "", label: loadingText }] : [...options];

  const isDisabled = disabled || loading;

  return (
    <FormControl required={required} sx={formControlSx}>
      <FormLabel sx={{ fontWeight: "bold" }}>{label}</FormLabel>
      <Select
        name={name}
        value={loading ? "" : value || ""} // Reset to empty when loading
        onChange={(_, newValue) => !loading && onChange(name, newValue || null)}
        disabled={isDisabled}
        sx={selectSx}
        slotProps={{
          button: {
            [`& .${selectClasses.indicator}`]: {
              transition: "0.2s",
              [`&.${selectClasses.expanded}`]: {
                transform: "rotate(-180deg)",
              },
            },
          },
        }}
        {...selectProps}
      >
        <Option value="">Select</Option>
        {allOptions.map((option) => (
          <Option key={option.value} value={option.value} disabled={option.value === "" || loading}>
            {option.label}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
};

interface TextareaInputProps extends BaseFieldProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  minRows?: number;
  disabled?: boolean;
  textareaSx?: SxProps;
  textareaProps?: Omit<TextareaProps, "name" | "value" | "onChange" | "placeholder" | "minRows" | "disabled" | "sx">;
  maxLength?: number; // New prop for custom max length
}

export const TextareaInput: React.FC<TextareaInputProps> = ({
  name,
  label,
  value,
  onChange,
  placeholder,
  minRows = 2,
  required,
  disabled,
  formControlSx,
  textareaSx,
  textareaProps,
  maxLength, // Default to 500 characters
}) => {
  const charactersUsed = value.length;
  const charactersRemaining = maxLength - charactersUsed;
  const isOverLimit = charactersUsed > maxLength;

  return (
    <FormControl required={required} sx={formControlSx}>
      <FormLabel sx={{ fontWeight: "bold" }}>{label}</FormLabel>
      <Textarea name={name} value={value} onChange={onChange} placeholder={placeholder} minRows={minRows} disabled={disabled} sx={textareaSx} {...textareaProps} />
      {!disabled && maxLength && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 0.5,
            fontSize: "0.75rem",
            color: isOverLimit ? "error.main" : "text.secondary",
          }}
        >
          {charactersRemaining <= 0 ? 0 : charactersRemaining} characters remaining
        </Box>
      )}
    </FormControl>
  );
};

export const percentageFormatter = (value: number): string => `${value.toFixed(1)}%`;

export const RadioInput: React.FC<RadioInputProps> = ({ name, label, value, onChange, options, required = false, disabled = false }) => {
  return (
    <FormControl required={required}>
      <FormLabel sx={{ fontWeight: "bold" }}>{label}</FormLabel>
      <RadioGroup name={name} value={value} onChange={onChange} orientation="horizontal">
        {options.map((option) => (
          <Radio key={option.value} value={option.value} label={option.label} disabled={disabled} />
        ))}
      </RadioGroup>
    </FormControl>
  );
};
